import React, { useEffect, useRef, useState } from "react";
import { css, keyframes } from "styled-components";

// ---------------------------------------------------------------------------
// Keyframes
// ---------------------------------------------------------------------------

export const fadeUpKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ---------------------------------------------------------------------------
// Animation CSS helpers
// ---------------------------------------------------------------------------

export const fadeUpAnimation = (delay = 0, duration = 600) => css`
  animation: ${fadeUpKeyframes} ${duration}ms ease-out ${delay}ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
    transform: none;
  }
`;

// For scroll-revealed elements: initial hidden state
export const hiddenForReveal = css`
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 600ms ease-out, transform 600ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

export const visibleAfterReveal = css`
  opacity: 1;
  transform: translateY(0);
`;

// ---------------------------------------------------------------------------
// useScrollReveal hook
// ---------------------------------------------------------------------------

export function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced motion — mark visible immediately
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

// ---------------------------------------------------------------------------
// ScrollReveal wrapper component
// ---------------------------------------------------------------------------

interface ScrollRevealProps {
  children: React.ReactNode;
  delay?: number;
  threshold?: number;
  style?: React.CSSProperties;
  className?: string;
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  delay = 0,
  threshold = 0.15,
  style,
  className,
}) => {
  const { ref, visible } = useScrollReveal(threshold);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      className={className}
      style={{
        opacity: reducedMotion || visible ? 1 : 0,
        transform:
          reducedMotion || visible ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 600ms ease-out ${delay}ms, transform 600ms ease-out ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
};
